// Net utility functions for schematic editor and stick diagram.
// Handles net name resolution, traversal and propagation across connected edges.

export const getAllNetEdges = (graph, startEdge) => {
  const model = graph.getModel();
  const visited = new Set();
  const result = [];
  const queue = [startEdge];
  visited.add(startEdge.id);

  while (queue.length > 0) {
    const edge = queue.shift();
    result.push(edge);

    [model.getTerminal(edge, true), model.getTerminal(edge, false)].forEach(terminal => {
      if (!terminal) return;
      if (model.isEdge(terminal) && !visited.has(terminal.id)) {
        visited.add(terminal.id);
        queue.push(terminal);
      }
      if (model.isVertex(terminal)) {
        graph.getEdges(terminal).forEach(ce => {
          if (!visited.has(ce.id)) { visited.add(ce.id); queue.push(ce); }
        });
      }
    });

    graph.getEdges(edge).forEach(ce => {
      if (!visited.has(ce.id)) { visited.add(ce.id); queue.push(ce); }
    });
  }

  return result;
};

// Returns true if a cell is a named pin (in/out/inout shape with a label).
export const isPinCell = (graph, cell) => {
  const model = graph.getModel();
  if (!cell || !model.isVertex(cell) || !cell.value) return false;
  const style = model.getStyle(cell) || '';
  return style.includes('shape=in') || style.includes('shape=out') || style.includes('shape=inout');
};

export const isNoConnPort = (graph, cell) => {
  if (!cell) return false;
  const model = graph.getModel();
  const parent = model.getParent(cell);
  if (!parent) return false;
  return (model.getStyle(parent) || '').includes('shape=no-conn');
};

// Resolves the best net name for a cell (pin or wire terminal).
export const getConnectedNetName = (graph, cell, noConnCounterRef, tabKey) => {
  if (!cell) return null;
  const model = graph.getModel();
 
  // Priority 1: no-conn port — must be before pin check
  if (noConnCounterRef && tabKey && isNoConnPort(graph, cell)) {
    if (!noConnCounterRef.current[tabKey]) noConnCounterRef.current[tabKey] = 1;
    return `no-conn_${noConnCounterRef.current[tabKey]++}`;
  }
 
  // Connecting directly onto an existing wire — inherit its net name
  if (model.isEdge(cell) && cell.value && cell.value.trim() !== '') {
    return cell.value;
  }
 
  // Priority 2: named pin — use pin name
  if (isPinCell(graph, cell)) {
    return cell.value;
  }
 
  // Priority 3: check edges already connected to this vertex
  // Pin name > user label > auto name
  const connectedEdges = graph.getEdges(cell);
  let pinFallback = null;
  let userLabelFallback = null;
  let autoFallback = null;
 
  for (const edge of connectedEdges) {
    if (!edge.value || edge.value.trim() === '') continue;
 
    // no-conn_* on existing edge — highest priority
    if (edge.value.startsWith('no-conn_')) return edge.value;
 
    const edgeStyle = edge.style || '';
    const isUserLabel = !edgeStyle.includes('noLabel=1');
 
    if (!isUserLabel && !pinFallback) {
      // auto name — check if terminal is a pin
      const src = model.getTerminal(edge, true);
      const tgt = model.getTerminal(edge, false);
      if (isPinCell(graph, src) || isPinCell(graph, tgt)) {
        pinFallback = edge.value; // pin-derived net name
      } else {
        autoFallback = edge.value;
      }
    } else if (isUserLabel && !userLabelFallback) {
      userLabelFallback = edge.value;
    }
  }
 
  // Return in priority order: pin > user label > auto
  return pinFallback || userLabelFallback || autoFallback || null;
};

/**
 * After a new edge is added, find the highest-priority net name across the
 * entire connected net and propagate it to all edges in that net.
 * Priority: user label > pin name > auto name
 */
export const propagateBestNetName = (graph, edge, source, target) => {
  const model = graph.getModel();
  const connectedEdges = getAllNetEdges(graph, edge);
 
  let bestName = null;
  let bestIsPinName = false;
  let bestIsUserLabel = false;
 
  // Priority 1: no-conn_* — check all edges first
  const noConnName = connectedEdges
    .map((e) => e.value)
    .find((v) => v?.startsWith('no-conn_'));
 
  if (noConnName) {
    connectedEdges.forEach((e) => model.setValue(e, noConnName));
    return;
  }
 
  // Priority 2: pin name from source/target
  if (isPinCell(graph, source)) {
    bestName = source.value;
    bestIsPinName = true;
  } else if (isPinCell(graph, target)) {
    bestName = target.value;
    bestIsPinName = true;
  }
 
  // Priority 3: user label (no noLabel=1) — only wins over auto name, not pin
  connectedEdges.forEach((e) => {
    if (!e.value || e.value.trim() === '') return;
    const edgeStyle = e.style || '';
    const isUserLabel = !edgeStyle.includes('noLabel=1');
 
    if (isUserLabel && !bestIsPinName) {
      // User label wins over auto name but not over pin name
      bestName = e.value;
      bestIsUserLabel = true;
    } else if (!bestIsUserLabel && !bestIsPinName && !bestName) {
      // Auto name fallback
      bestName = e.value;
    }
  });
 
  if (bestName) {
    connectedEdges.forEach((e) => model.setValue(e, bestName));
  }
};

/**
 * Re-evaluate the net name for a group of connected edges after a wire is deleted.
 * Priority (descending): user label (noLabel absent) > pin connection > auto name.
 * Called with any surviving edge in the net as the starting point.
 */
export const reEvaluateNetName = (graph, startEdge, netCountersRef, tabKey) => {
  const model = graph.getModel();
  const netEdges = getAllNetEdges(graph, startEdge);

  // 1. Keep user-defined label — don't touch anything
  for (const edge of netEdges) {
    if (edge.value && edge.value.trim() !== '') {
      const s = edge.style || '';
      if (!s.includes('noLabel=1')) return;
    }
  }

  // 2. Check if any terminal of the remaining edges is still a named pin
  let pinName = null;
  for (const edge of netEdges) {
    for (const isSource of [true, false]) {
      const terminal = model.getTerminal(edge, isSource);
      if (isPinCell(graph, terminal)) {
        pinName = terminal.value;
        break;
      }
    }
    if (pinName) break;
  }

  if (pinName) {
    netEdges.forEach(e => model.setValue(e, pinName));
    return;
  }

  // 3. No pin, no user label — assign a fresh auto name to the whole net
  if (!netCountersRef.current[tabKey]) netCountersRef.current[tabKey] = 1;
  const freshName = `net_${netCountersRef.current[tabKey]++}`;
  netEdges.forEach(edge => {
    model.setValue(edge, freshName);
    let style = edge.style || '';
    if (!style.includes('noLabel=1')) style += ';noLabel=1';
    model.setStyle(edge, style);
  });
};

/**
 * Apply a net label to an edge and all electrically connected edges.
 * When label is set: show it on the labeled edge, hide on others.
 * When label is cleared: restore a fresh hidden auto net name on all.
 */
export const applyNetLabel = (graph, editingEdge, newLabel, isDarkMode, netCountersRef, activeTab) => {
  const model = graph.getModel();
  const fc = isDarkMode ? '#ffffff' : '#000000';
  const netEdges = getAllNetEdges(graph, editingEdge);

  if (newLabel) {
    netEdges.forEach(edge => {
      model.setValue(edge, newLabel);
      let style = edge.style || '';
      if (edge === editingEdge) {
        style = style.replace(/;?noLabel=1/g, '');
      } else {
        if (!style.includes('noLabel=1')) style += ';noLabel=1';
      }
      if (style.includes('fontColor=')) {
        style = style.replace(/fontColor=[^;]*/, `fontColor=${fc}`);
      } else {
        style += `;fontColor=${fc}`;
      }
      if (style.includes('labelBackgroundColor=')) {
        style = style.replace(/labelBackgroundColor=[^;]*/, 'labelBackgroundColor=none');
      } else {
        style += ';labelBackgroundColor=none';
      }
      model.setStyle(edge, style);
    });
  } else {
    if (!netCountersRef.current[activeTab]) netCountersRef.current[activeTab] = 1;
    const restoredName = `net_${netCountersRef.current[activeTab]++}`;
    netEdges.forEach(edge => {
      model.setValue(edge, restoredName);
      let style = edge.style || '';
      if (!style.includes('noLabel=1')) style += ';noLabel=1';
      model.setStyle(edge, style);
    });
  }
};
